import { useCallback, useState, type ComponentType, type ErrorInfo } from 'react';

import { sentryCapture } from '../../sentry/sentryCapture';

import ErrorBoundary from './ErrorBoundary';
import ErrorComponent from './ErrorComponent';

interface WithErrorBoundaryOptions {
  errorTag: string;
}

interface UseErrorBoundaryReturn {
  onError: (error: Error, errorInfo: ErrorInfo) => void;
  caughtError: string;
}

const useErrorBoundary = ({ errorTag }: WithErrorBoundaryOptions): UseErrorBoundaryReturn => {
  const [caughtError, setCaughtError] = useState<string>('');

  const onError = useCallback(
    (error: Error, errorInfo: ErrorInfo) => {
      sentryCapture(error, errorTag, error, errorInfo?.componentStack ?? undefined);
      setCaughtError(error.toString());
    },
    [errorTag],
  );

  return { onError, caughtError };
};

export const withErrorBoundary = <P extends object>(
  Component: ComponentType<P>,
  { errorTag }: WithErrorBoundaryOptions,
) => {
  const WithErrorBoundaryComponent = (props: P) => {
    const { onError, caughtError } = useErrorBoundary({ errorTag });

    return (
      <ErrorBoundary onError={onError} fallback={<ErrorComponent error={caughtError} />}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };

  return WithErrorBoundaryComponent;
};
